/**
 * @architecture-header 1
 * @layer module
 * @domain curriculum
 * @responsibility Create, replace, archive, and reopen Curriculum units.
 * @state-owner TanStack Query mutation state
 * @allowed-imports app environment; TanStack Query; module query and model
 * @forbidden routes; components; direct generated SDK
 * @public-api via modules/curriculum/index.ts
 * @side-effects authenticated Curriculum mutation requests outside demo mode
 * @naming-hook POST -> useCreate<Resource>; PATCH entity -> useUpdate<Resource>
 * @naming-path plural lowercase nouns; organization is the active-context scope hint
 * @naming-body camelCase; never caller role and never a server-side key
 * @tests curriculum mutation tests
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useWorkspaceEnvironment } from '../../../app/config/environment';
import type { CurriculumUnit } from '../model/types';
import { curriculumKeys, parseCurriculumUnit, updateCurriculumFixture } from './curriculum.queries';

const curriculumClassroomPath = '/api/v1/classrooms';
const curriculumUnitPath = '/api/v1/curriculum-units';

export function useCreateCurriculumUnit() {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      activeContextId: string;
      organizationId: string;
      classroomId: string;
      name: string;
      knowledgeComponentIds: readonly string[];
      idempotencyKey: string;
    }) => {
      if (environment.demoMode) {
        const position =
          Math.max(
            -1,
            ...(
              (
                globalThis as typeof globalThis & {
                  __VISPO_CUR_F01_FIXTURE__?: { units?: CurriculumUnit[] };
                }
              ).__VISPO_CUR_F01_FIXTURE__?.units ?? []
            )
              .filter((unit) => !unit.archived)
              .map((unit) => unit.position),
          ) + 1;
        const unit = {
          id: globalThis.crypto?.randomUUID?.() ?? '00000000-0000-4000-8000-000000000099',
          organization_id: input.organizationId,
          classroom_id: input.classroomId,
          name: input.name.trim(),
          position,
          revision: 1,
          archived: false,
          archived_at: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          knowledge_components: [],
        } satisfies CurriculumUnit;
        updateCurriculumFixture((units) => [...units, unit]);
        return unit;
      }
      return parseCurriculumUnit(
        await environment.apiClient.request<unknown>(
          `${curriculumClassroomPath}/${encodeURIComponent(input.classroomId)}/curriculum-units`,
          {
            method: 'POST',
            body: {
              organizationId: input.organizationId,
              name: input.name.trim(),
              knowledgeComponentIds: input.knowledgeComponentIds,
              idempotencyKey: input.idempotencyKey,
            },
          },
        ),
      );
    },
    onSuccess: (_unit, input) =>
      queryClient.invalidateQueries({
        queryKey: curriculumKeys.all(input.activeContextId, input.classroomId, 'active'),
      }),
  });
}

export function useUpdateCurriculumUnit() {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      activeContextId: string;
      organizationId: string;
      classroomId: string;
      unit: CurriculumUnit;
      name?: string;
      knowledgeComponentIds?: readonly string[];
      archived?: boolean;
      idempotencyKey: string;
    }) => {
      if (environment.demoMode) {
        const fixtureUnits =
          (
            globalThis as typeof globalThis & {
              __VISPO_CUR_F01_FIXTURE__?: { units?: CurriculumUnit[] };
            }
          ).__VISPO_CUR_F01_FIXTURE__?.units ?? [];
        const nextPosition =
          Math.max(
            -1,
            ...fixtureUnits.filter((unit) => !unit.archived).map((unit) => unit.position),
          ) + 1;
        const updated: CurriculumUnit = {
          ...input.unit,
          ...(input.name === undefined ? {} : { name: input.name.trim() }),
          ...(input.archived === undefined
            ? {}
            : {
                archived: input.archived,
                archived_at: input.archived ? new Date().toISOString() : null,
                ...(input.archived ? {} : { position: nextPosition }),
              }),
          ...(input.knowledgeComponentIds === undefined
            ? {}
            : {
                knowledge_components: input.knowledgeComponentIds.map((id, position) => ({
                  id,
                  name: id,
                  description: '',
                  position,
                })),
              }),
          revision: input.unit.revision + 1,
          updated_at: new Date().toISOString(),
        };
        updateCurriculumFixture((units) =>
          units.map((unit) => (unit.id === input.unit.id ? updated : unit)),
        );
        return updated;
      }
      return parseCurriculumUnit(
        await environment.apiClient.request<unknown>(
          `${curriculumUnitPath}/${encodeURIComponent(input.unit.id)}`,
          {
            method: 'PATCH',
            body: {
              organizationId: input.organizationId,
              ...(input.name === undefined ? {} : { name: input.name.trim() }),
              ...(input.knowledgeComponentIds === undefined
                ? {}
                : { knowledgeComponentIds: input.knowledgeComponentIds }),
              ...(input.archived === undefined ? {} : { archived: input.archived }),
              expectedRevision: input.unit.revision,
              idempotencyKey: input.idempotencyKey,
            },
          },
        ),
      );
    },
    onSuccess: (_unit, input) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: curriculumKeys.all(input.activeContextId, input.classroomId, 'active'),
        }),
        queryClient.invalidateQueries({
          queryKey: curriculumKeys.all(input.activeContextId, input.classroomId, 'archived'),
        }),
      ]),
  });
}
